const API_BASE = '/api';

interface ApiError {
  // Fastify default: { statusCode, code?, error: "Bad Request", message }
  // Custom shape: { error: { code, message, details? } }
  statusCode?: number;
  code?: string;
  error?: string | { code: string; message: string; details?: Record<string, unknown> };
  message?: string;
}

class ApiClient {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({}));
      const message =
        (typeof errorData.error === 'object' && errorData.error?.message) ||
        errorData.message ||
        (typeof errorData.error === 'string' ? errorData.error : undefined) ||
        `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Typed API functions
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'pending';
  createdAt: string;
  updatedAt: string;
  mappings: Array<{
    id: string;
    emailDomain: string;
    isPrimary: boolean;
  }>;
  userCount?: number;
}

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  tenantId: string;
  status: 'active' | 'suspended' | 'pending_invitation';
  lastLoginAt: string | null;
  createdAt: string;
  roles: Array<{ id: string; name: string }>;
  appAccess: Array<{ appId: string; isBlocked: boolean }>;
}

export interface App {
  id: string;
  name: string;
  description: string;
  baseUrl: string;
  category: string;
  icon: string;
  order: number;
  status?: 'active' | 'maintenance' | 'disabled';
  manifest?: Record<string, unknown>;
}

export interface AuditEvent {
  event_id: string;
  occurred_at: string;
  tenant_id: string;
  actor: {
    type: 'user' | 'system' | 'public';
    user_id?: string;
    email?: string;
  };
  source: {
    app_id: string;
    environment: string;
  };
  action: string;
  target?: {
    type: string;
    id: string;
  };
  metadata?: Record<string, unknown>;
}

export interface Role {
  id: string;
  tenant_id: string | null;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: Array<{
    id: string;
    action: string;
    description: string;
  }>;
}

// Shape returned by /permissions/v1/roles (permissions collapsed to action
// strings instead of the full {id, action, description} triple).
export interface RoleDetail {
  id: string;
  tenant_id: string | null;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export interface PermissionEntry {
  id: string;
  action: string;
  description: string;
  app_id: string | null;
}

// Build a querystring that omits undefined/null values so we don't send
// literal "undefined" strings to the backend (which would then be used as
// a real filter value and match nothing).
function qs(params?: Record<string, unknown>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

// API Functions
export const tenantsApi = {
  list: (params?: { limit?: number; offset?: number; query?: string }) =>
    api.get<PaginatedResponse<Tenant>>(`/identity/v1/tenants${qs(params)}`),
  get: (id: string) => api.get<Tenant>(`/identity/v1/tenants/${id}`),
  create: (data: { name: string; slug: string; primaryDomain: string }) =>
    api.post<Tenant>('/identity/v1/tenants', data),
  update: (id: string, data: { name?: string; status?: string }) =>
    api.patch<Tenant>(`/identity/v1/tenants/${id}`, data),
};

export const usersApi = {
  list: (params?: { tenantId?: string; limit?: number; offset?: number; query?: string; status?: string }) =>
    api.get<PaginatedResponse<User>>(`/identity/v1/users${qs(params)}`),
  get: (id: string) => api.get<User>(`/identity/v1/users/${id}`),
  invite: (data: { email: string; tenantId: string; roleIds: string[] }) =>
    api.post<{ invitationId: string; emailSent: boolean; emailError?: string }>(
      '/identity/v1/users/invite',
      data
    ),
  update: (id: string, data: { displayName?: string; status?: string }) =>
    api.patch<User>(`/identity/v1/users/${id}`, data),
  updateRoles: (id: string, roleIds: string[]) =>
    api.put<User>(`/identity/v1/users/${id}/roles`, { roleIds }),
  updateAppAccess: (userId: string, appId: string, data: { isBlocked: boolean; blockedReason?: string }) =>
    api.put(`/identity/v1/users/${userId}/app-access/${appId}`, data),
};

export interface PendingInvitation {
  id: string;
  email: string;
  roleIds: string[];
  expiresAt: string;
  createdAt: string;
}

export const invitationsApi = {
  listForTenant: (tenantId: string) =>
    api.get<{ data: PendingInvitation[] }>(`/identity/v1/tenants/${tenantId}/invitations`),
  revoke: (id: string) =>
    api.delete<{ success: boolean }>(`/identity/v1/invitations/${id}`),
};

export const appsApi = {
  list: () => api.get<{ data: App[] }>('/apps/v1/apps'),
  getLauncher: () => api.get<{ apps: App[] }>('/apps/v1/launcher'),
  checkAccess: (appId: string) => api.post<{ hasAccess: boolean; reason?: string }>(`/apps/v1/apps/${appId}/check-access`),
};

export const auditApi = {
  list: (params?: {
    tenant_id?: string;
    user_id?: string;
    app_id?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => api.get<PaginatedResponse<AuditEvent>>(`/audit/v1/events${qs(params)}`),
  getEpoApprovals: (params?: { tenant_id?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }) =>
    api.get<PaginatedResponse<AuditEvent>>(`/audit/v1/events/epo-approvals${qs(params)}`),
  export: (format: 'json' | 'csv', filter?: Record<string, unknown>) =>
    api.post<string>('/audit/v1/events/export', { format, filter }),
};

export const permissionsApi = {
  getCatalog: () => api.get<{ roles: Role[]; permissions: PermissionEntry[] }>('/permissions/v1/catalog'),
};

export const rolesApi = {
  list: () => api.get<{ data: RoleDetail[] }>('/permissions/v1/roles'),
  listPermissions: () => api.get<{ data: PermissionEntry[] }>('/permissions/v1/permissions'),
  create: (data: { name: string; description?: string; permissions: string[] }) =>
    api.post<RoleDetail>('/permissions/v1/roles', data),
  update: (id: string, data: { name?: string; description?: string; permissions?: string[] }) =>
    api.patch<RoleDetail>(`/permissions/v1/roles/${id}`, data),
  remove: (id: string) =>
    api.delete<void>(`/permissions/v1/roles/${id}`),
};
